<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { onMounted, ref } from "vue";
import { SatItemDisplay } from "@chatfood/design-system";
import type { ICrossSellItemsEmits } from "./emits";
import { useBugReporter, useDirectBackend } from "#imports";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import { ItemModal } from "~/modals/item";
import { useNavigation } from "~/composables/use-navigation";

const css = bemBuilder("cross-sell-items");
const emits = defineEmits<ICrossSellItemsEmits>();

const business = useBusinessStore();
const basket = useBasketStore();
const outlet = useOutletStore();
const { basketRepo } = useDirectBackend();
const { report } = useBugReporter();
const menuId = ref("");
const { rawPush } = useNavigation();

const crossSellItems = ref<
  Awaited<ReturnType<typeof basketRepo.getCrossSellItems>>["data"]
>([]);

const fetchCrossSellItems = async () => {
  try {
    const res = await basketRepo.getCrossSellItems({
      businessId: business.id,
      outletId: outlet.id,
      orderingMode: basket.orderingMode,
      salesChannel: basket.salesChannel,
    });
    crossSellItems.value = res.data;
    emits("hasItems", res.data.length > 0);
  } catch (e) {
    emits("hasItems", false);
    report(e);
  }
};

const handleItemClick = (id: string, menu: string) => {
  menuId.value = menu;
  rawPush({
    query: {
      "item-id": id,
    },
  });
};

const handleItemModalHidden = () => {
  menuId.value = "";
};

onMounted(() => {
  fetchCrossSellItems();
});
</script>

<template>
  <div :class="css('')">
    <SatItemDisplay
      v-for="item in crossSellItems"
      :id="item.id"
      :key="item.id"
      :class="css('items')"
      view="col"
      :name="item.name"
      :description="item.description"
      :amount="item.price"
      :photo="item.photo"
      :has-allergen-items="Boolean(item.allergens)"
      :consumables="item.consumables"
      @click="handleItemClick(item.id, item.menuId)"
    />
    <ItemModal :menu-id="menuId" @hidden="handleItemModalHidden" />
  </div>
</template>

<style lang="scss" scoped>
.cross-sell-items {
  display: flex;
  overflow-y: scroll;
  padding-left: 14px;
  gap: var(--spacing-md);
  padding-bottom: 18px;
  width: 100%;

  &__items {
    min-width: 90%;
    border: 1px solid var(--color-base-80);
    border-radius: var(--radii-card-md);
    padding: var(--spacing-md);
    cursor: pointer;
  }
}
</style>
